<template>
  <div>
    <h4 class="tw-text-lg tw-font-semibold tw-mb-4">{{ t('users.titles.create') }}</h4>

    <Card>
      <CardHeader>
        <CardTitle class="tw-font-bold tw-text-[16px] tw-text-secondary-500 tw-m-0">
          {{ t('users.titles.user_data') }}
        </CardTitle>
      </CardHeader>
      <form @submit="onSubmitUserForm">
        <CardContent class="tw-p-6">
          <div class="tw-grid tw-grid-cols-2 tw-gap-x-8 tw-gap-y-6">
            <!-- First Name -->
            <div class="tw-space-y-1">
              <Label
                for="firstName"
                class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
              >
                {{ t('users.titles.first_name') + ' *' }}
              </Label>
              <Input
                id="firstName"
                v-model="firstName"
                :placeholder="t('users.forms.name_placeholder') + '...'"
                class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
                :class="{ 'tw-border-red-500': errors.firstName }"
              />
              <span class="tw-text-xs tw-text-red-500 tw-mt-1 tw-block tw-min-h-[20px]">
                {{ errors.firstName || '' }}
              </span>
            </div>

            <!-- Last Name -->
            <div class="tw-space-y-1">
              <Label
                for="lastName"
                class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
              >
                {{ t('users.titles.last_name') + ' *' }}
              </Label>
              <Input
                id="lastName"
                v-model="lastName"
                :placeholder="t('users.forms.name_placeholder') + '...'"
                class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
                :class="{ 'tw-border-red-500': errors.lastName }"
              />
              <span class="tw-text-xs tw-text-red-500 tw-mt-1 tw-block tw-min-h-[20px]">
                {{ errors.lastName || '' }}
              </span>
            </div>

            <!-- Email (full width) -->
            <div class="tw-space-y-1 tw-col-span-2">
              <Label
                for="userEmail"
                class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
              >
                {{ t('common.titles.email') + ' *' }}
              </Label>
              <Input
                id="userEmail"
                v-model="userEmail"
                type="email"
                :placeholder="t('users.forms.email_placeholder') + '...'"
                class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
                :class="{ 'tw-border-red-500': errors.userEmail }"
              />
              <span class="tw-text-xs tw-text-red-500 tw-mt-1 tw-block tw-min-h-[20px]">
                {{ errors.userEmail || '' }}
              </span>
            </div>

            <!-- Is Admin -->
            <div class="tw-flex tw-items-center tw-gap-3 tw-col-span-2">
              <Switch id="isAdmin" :model-value="isAdmin" @update:model-value="isAdmin = $event" />
              <Label for="isAdmin" class="tw-text-sm tw-font-medium tw-text-secondary-700">
                {{ t('users.titles.is_admin') }}
              </Label>
            </div>

            <!-- Roles & Clearing Groups (disabled when admin) -->
            <div
              class="tw-col-span-2 tw-grid tw-grid-cols-2 tw-gap-x-8 tw-gap-y-6 tw-transition-opacity"
              :class="{ 'tw-opacity-50': isAdmin }"
              :style="isAdmin ? { pointerEvents: 'none' } : {}"
            >
              <!-- Roles (full width) -->
              <div class="tw-space-y-1 tw-col-span-2">
                <Label class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block">
                  {{ t('users.titles.role') }}
                </Label>
                <Select
                  multiple
                  :model-value="userRoles"
                  @update:model-value="userRoles = $event as string[]"
                  :disabled="isAdmin"
                >
                  <SelectTrigger
                    class="tw-w-full tw-min-h-[48px] tw-h-auto tw-px-3 tw-py-2 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
                  >
                    <SelectValue :placeholder="t('users.titles.select_roles')" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      v-for="(label, key) in selectableRolesOptions"
                      :key="key"
                      :value="key"
                    >
                      {{ label }}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <!-- Order Clearing Group -->
              <div class="tw-space-y-1">
                <Label
                  for="orderClearingGroup"
                  class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
                >
                  {{ t('users.titles.order_clearing_group') }}
                </Label>
                <Select
                  :model-value="orderClearingGroup != null ? String(orderClearingGroup) : 'none'"
                  @update:model-value="onOrderGroupChange"
                  :disabled="isAdmin || !selectedCustomer"
                >
                  <SelectTrigger
                    class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
                  >
                    <SelectValue :placeholder="t('common.titles.none')" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{{ t('common.titles.none') }}</SelectItem>
                    <SelectItem
                      v-for="group in orderGroups"
                      :key="group.id"
                      :value="String(group.id)"
                    >
                      {{ group.name }}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <!-- Payment Clearing Group -->
              <div class="tw-space-y-1">
                <Label
                  for="paymentClearingGroup"
                  class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
                >
                  {{ t('users.titles.payment_clearing_group') }}
                </Label>
                <Select
                  :model-value="
                    paymentClearingGroup != null ? String(paymentClearingGroup) : 'none'
                  "
                  @update:model-value="onPaymentGroupChange"
                  :disabled="isAdmin || !selectedCustomer"
                >
                  <SelectTrigger
                    class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
                  >
                    <SelectValue :placeholder="t('common.titles.none')" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{{ t('common.titles.none') }}</SelectItem>
                    <SelectItem
                      v-for="group in paymentGroups"
                      :key="group.id"
                      :value="String(group.id)"
                    >
                      {{ group.name }}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>

        <div class="tw-flex tw-gap-2 tw-justify-end tw-px-6 tw-pb-6">
          <Button type="button" variant="outline" @click="backToUsersList" :disabled="isSubmitting">
            {{ t('common.forms.discard') }}
          </Button>
          <Button type="submit" :disabled="isSubmitting">
            {{ t('users.titles.add') }}
          </Button>
        </div>
      </form>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useField, useForm } from 'vee-validate';
import type { AcceptableValue } from 'reka-ui';
import { onBeforeMount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toast-notification';
import * as z from 'zod';

import { UserRoles } from '@/_helpers/interfaces/users/current-user.interface';
import cookieService from '@/_helpers/services/cookie.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import router from '@/router';
import { useCustomerStore } from '@/stores/customer.store';
import { useUserStore } from '@/stores/user.store';

const { t } = useI18n();
const { setBreadcrumbs } = useBreadcrumbs();
const toast = useToast();
const customerStore = useCustomerStore();
const { addUser } = useUserStore();

// Selectable roles: exclude ADMIN, ORDER_CLEARER, PAYMENT_CLEARER
const selectableRolesOptions: Record<string, string> = {};
for (const [key, label] of Object.entries(UserRoles)) {
  if (!['ADMIN', 'ORDER_CLEARER', 'PAYMENT_CLEARER'].includes(key)) {
    selectableRolesOptions[key] = label;
  }
}

// Clearing groups state
const selectedCustomer = ref<number | null>(null);
const orderGroups = ref<Array<{ id: number; name: string }>>([]);
const paymentGroups = ref<Array<{ id: number; name: string }>>([]);

// Zod schema
const formSchema = toTypedSchema(
  z.object({
    firstName: z.string().min(1, t('users.titles.first_name') + ' is required'),
    lastName: z.string().min(1, t('users.titles.last_name') + ' is required'),
    userEmail: z
      .string()
      .min(1, t('common.titles.email') + ' is required')
      .email(t('common.titles.email') + ' is invalid'),
    isAdmin: z.boolean().default(false),
    userRoles: z.array(z.string()).default([]),
    orderClearingGroup: z.number().nullable().optional(),
    paymentClearingGroup: z.number().nullable().optional(),
  })
);

const { errors, handleSubmit, isSubmitting, resetForm } = useForm({
  validationSchema: formSchema,
  initialValues: {
    firstName: '',
    lastName: '',
    userEmail: '',
    isAdmin: false,
    userRoles: [],
    orderClearingGroup: null,
    paymentClearingGroup: null,
  },
});

const { value: firstName } = useField<string>('firstName');
const { value: lastName } = useField<string>('lastName');
const { value: userEmail } = useField<string>('userEmail');
const { value: isAdmin } = useField<boolean>('isAdmin');
const { value: userRoles } = useField<string[]>('userRoles');
const { value: orderClearingGroup } = useField<number | null>('orderClearingGroup');
const { value: paymentClearingGroup } = useField<number | null>('paymentClearingGroup');

const onOrderGroupChange = (val: AcceptableValue) => {
  orderClearingGroup.value = val === 'none' ? null : Number(val);
};

const onPaymentGroupChange = (val: AcceptableValue) => {
  paymentClearingGroup.value = val === 'none' ? null : Number(val);
};

// Fetch clearing groups when customer is available
const fetchClearingGroups = async (customerId: number) => {
  try {
    const data = await customerStore.getClearingGroups(customerId);
    orderGroups.value = data.order_groups || [];
    paymentGroups.value = data.payment_groups || [];
  } catch {
    orderGroups.value = [];
    paymentGroups.value = [];
  }
};

// Watch admin toggle: clear roles and clearing groups when admin
watch(isAdmin, newVal => {
  if (newVal) {
    userRoles.value = [];
    orderClearingGroup.value = null;
    paymentClearingGroup.value = null;
  }
});

const onSubmitUserForm = handleSubmit(async values => {
  try {
    const payload: Parameters<typeof addUser>[0] = {
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.userEmail,
      is_admin: values.isAdmin,
    };

    if (!values.isAdmin && values.userRoles.length > 0) {
      payload.roles = [...values.userRoles];
    }

    if (!values.isAdmin && selectedCustomer.value) {
      payload.customer = selectedCustomer.value;
    }

    if (!values.isAdmin && values.orderClearingGroup != null) {
      payload.order_clearing_group = values.orderClearingGroup;
    }

    if (!values.isAdmin && values.paymentClearingGroup != null) {
      payload.payment_clearing_group = values.paymentClearingGroup;
    }

    await addUser(payload);

    toast.open({
      message: t('users.forms.create_success'),
      type: 'success',
    });

    resetForm();
  } catch (error: any) {
    const message =
      error?.response?.data?.errors?.[0]?.detail || t('common.errors.something_went_wrong');
    toast.open({
      message,
      type: 'error',
    });
  }
});

const backToUsersList = () => {
  router.push({ name: 'list-users' });
};

onBeforeMount(async () => {
  setBreadcrumbs([
    {
      labelKey: 'sidebar.items.users',
      to: { name: 'list-users' },
    },
    {
      labelKey: 'common.actions.create',
    },
  ]);

  const savedCustomerId = cookieService.get('selectedCustomerId');
  if (savedCustomerId) {
    selectedCustomer.value = parseInt(savedCustomerId);
    await fetchClearingGroups(selectedCustomer.value);
  }
});
</script>
